import React, { useEffect, useState } from "react";
import { AUTH_STORAGE_KEY, SERVER_URL } from "../../socket";

function ChatSettings() {
  // ── Quick Replies ──────────────────────────────────────────────────────────
  const [quickReplies,  setQuickReplies]  = useState([]);
  const [newShortcut,   setNewShortcut]   = useState("");
  const [newMessage,    setNewMessage]    = useState("");
  const [qrMessage,     setQrMessage]     = useState(null);
  const [qrLoading,     setQrLoading]     = useState(false);
 const [enabled,  setEnabled]  = useState(false);
  const [message,  setMessage]  = useState("");
  const [original, setOriginal] = useState({ enabled: false, message: "" });
  const [loading,  setLoading]  = useState(false);
  const [status,   setStatus]   = useState(null); // { type, text }

  // ── Chat History ───────────────────────────────────────────────────────────
  const [historyMsg,    setHistoryMsg]    = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [backing,       setBacking]       = useState(false);
  const [backupDone,    setBackupDone]    = useState(false);

  const getAuthHeaders = () => {
    try {
      const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : null;
    } catch { return null; }
  };

  // ── Load quick replies ─────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      const headers = getAuthHeaders();
      if (!headers) return;
      try {
        const res  = await fetch(`${SERVER_URL}/api/settings/quick-replies`, { headers });
        const data = await res.json();
        setQuickReplies(Array.isArray(data?.quickReplies) ? data.quickReplies : []);
      } catch (_) {}
    };
    load();

    const handler = (e) => setQuickReplies(e.detail || []);
    window.addEventListener("quick-replies-updated", handler);
    return () => window.removeEventListener("quick-replies-updated", handler);
  }, []);

  // ── Add quick reply ────────────────────────────────────────────────────────
  const handleAddQR = async (e) => {
    e.preventDefault();
    const shortcut = newShortcut.trim().replace(/^\/+/, "");
    const message  = newMessage.trim();

    if (!shortcut || !message) {
      setQrMessage({ type: "error", text: "Both shortcut and message are required." });
      return;
    }
    if (quickReplies.some(r => r.shortcut === shortcut)) {
      setQrMessage({ type: "error", text: `Shortcut "/${shortcut}" already exists.` });
      return;
    }

    setQrLoading(true);
    const updated = [...quickReplies, { shortcut, message }];

    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${SERVER_URL}/api/settings/quick-replies`, {
        method:  "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body:    JSON.stringify({ quickReplies: updated }),
      });
      if (!res.ok) throw new Error();
      setQuickReplies(updated);
      setNewShortcut("");
      setNewMessage("");
      setQrMessage({ type: "success", text: "Quick reply added." });
      setTimeout(() => setQrMessage(null), 2000);
      window.dispatchEvent(new CustomEvent("quick-replies-updated", { detail: updated }));
    } catch {
      setQrMessage({ type: "error", text: "Failed to save quick reply." });
    }
    setQrLoading(false);
  };
  useEffect(() => {
    const load = async () => {
      const headers = getAuthHeaders();
      if (!headers) return;
      try {
        const res  = await fetch(`${SERVER_URL}/api/settings/auto-reply`, { headers });
        const data = await res.json();
        const s = data?.settings || {};
        setEnabled(Boolean(s.enabled));
        setMessage(s.message || "");
        setOriginal({ enabled: Boolean(s.enabled), message: s.message || "" });
      } catch (_) {}
    };
    load();
  }, []);

  // ── Delete quick reply ─────────────────────────────────────────────────────
  const handleDeleteQR = async (shortcut) => {
    const updated = quickReplies.filter(r => r.shortcut !== shortcut);
    try {
      const headers = getAuthHeaders();
      await fetch(`${SERVER_URL}/api/settings/quick-replies`, {
        method:  "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body:    JSON.stringify({ quickReplies: updated }),
      });
      setQuickReplies(updated);
      window.dispatchEvent(new CustomEvent("quick-replies-updated", { detail: updated }));
    } catch {
      setQrMessage({ type: "error", text: "Failed to delete." });
    }
  };

  // ── Delete ALL chat history ────────────────────────────────────────────────
  const handleDeleteAll = async () => {
    if (!window.confirm(
      "Delete ALL your chat history?\n\nThis cannot be undone."
    )) return;

    setDeleting(true);
    setHistoryMsg(null);
    try {
      const headers = getAuthHeaders();
      const res     = await fetch(`${SERVER_URL}/api/messages/history/all`, {
        method:  "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (res.ok) {
        setHistoryMsg({ type: "success", text: `All chat history deleted (${data.deleted} messages).` });
      } else {
        setHistoryMsg({ type: "error", text: data?.error || "Delete failed." });
      }
    } catch {
      setHistoryMsg({ type: "error", text: "Failed to delete chat history." });
    }
    setDeleting(false);
  };
 const handleSave = async (e) => {
    e?.preventDefault();
    if (enabled && !message.trim()) {
      setStatus({ type: "error", text: "Please enter an auto-reply message." });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${SERVER_URL}/api/settings/auto-reply`, {
        method:  "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body:    JSON.stringify({ enabled, message: message.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus({ type: "error", text: data?.error || "Failed to save." });
      } else {
        setOriginal({ enabled, message: message.trim() });
        setStatus({ type: "success", text: enabled ? "Auto-reply is now active." : "Auto-reply disabled." });
        setTimeout(() => setStatus(null), 2500);
      }
    } catch {
      setStatus({ type: "error", text: "Failed to save auto-reply." });
    }
    setLoading(false);
  };

  const isDirty =
    enabled !== original.enabled || message.trim() !== original.message;

  // ── Backup chat history ────────────────────────────────────────────────────
  const handleBackup = async () => {
    setBacking(true);
    setHistoryMsg(null);
    try {
      const headers = getAuthHeaders();
      const res     = await fetch(`${SERVER_URL}/api/messages/history/backup`, { headers });
      if (!res.ok) throw new Error("Backup failed");

      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `chat-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setBackupDone(true);
      setHistoryMsg({ type: "success", text: `Backup downloaded — ${data.totalMessages} messages.` });
      setTimeout(() => setHistoryMsg(null), 3000);
    } catch {
      setHistoryMsg({ type: "error", text: "Failed to backup chat history." });
    }
    setBacking(false);
  };

  // ── UI ─────────────────────────────────────────────────────────────────────
  return (
    <div className="settings-details-panel">
      <div className="settings-panel-header">
        <h4>
          <i className="fa-solid fa-comment-dots"></i> Chat
        </h4>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* QUICK REPLIES                                                        */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="settings-form">
        <p style={{ color: "#64748b", fontSize: 13, marginBottom: 20 }}>
          When someone sends you a message, an automatic reply is sent back
          instantly. Useful for away messages, greetings, or support acknowledgements.
        </p>

        {status && (
          <div
            className={`mail-settings-message ${status.type === "error" ? "error" : "success"}`}
            style={{ marginBottom: 16 }}
          >
            {status.text}
          </div>
        )}

        {/* ── Enable toggle ─────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 16px",
            border: `2px solid ${enabled ? "#1e40af" : "#e2e8f0"}`,
            borderRadius: 10,
            background: enabled ? "#eff6ff" : "transparent",
            marginBottom: 16,
            transition: "border .2s, background .2s",
          }}
        >
          <div>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>
              {enabled ? "🟢 Auto-reply is ON" : "⚪ Auto-reply is OFF"}
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {enabled
                ? "Anyone who messages you will receive your auto-reply."
                : "Enable to automatically reply to incoming messages."}
            </div>
          </div>

          <label className="switch" style={{ flexShrink: 0, marginLeft: 16 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span className="slider" />
          </label>
        </div>

        {/* ── Message input ────────────────────────────────────────────── */}
        <div className="settings-form-group">
          <label style={{ fontWeight: 500, display: "block", marginBottom: 6 }}>
            Auto-reply message
          </label>
          <textarea
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              enabled
                ? "e.g. Thanks for reaching out! I'll get back to you shortly."
                : "Enable auto-reply above to activate this message."
            }
            maxLength={500}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 14,
              resize: "vertical",
              minHeight: 90,
              opacity: enabled ? 1 : 0.5,
              boxSizing: "border-box",
            }}
          />
          <div style={{ textAlign: "right", fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
            {message.length} / 500
          </div>
        </div>

        {/* ── Save button ───────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={loading || !isDirty}
            style={{ opacity: (!isDirty || loading) ? 0.6 : 1 }}
          >
            {loading ? "Saving…" : "Save"}
          </button>
          {isDirty && (
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                setEnabled(original.enabled);
                setMessage(original.message);
                setStatus(null);
              }}
            >
              Cancel
            </button>
          )}
        </div>

        {/* ── Info box ─────────────────────────────────────────────────── */}
        {enabled && message.trim() && (
          <div style={{
            marginTop: 20,
            padding: "12px 16px",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 8,
            fontSize: 13,
          }}>
            <strong style={{ color: "#15803d" }}>Preview:</strong>
            <div style={{ marginTop: 6, color: "#166534" }}>
              "{message.trim()}"
            </div>
            <div style={{ marginTop: 6, color: "#94a3b8", fontSize: 12 }}>
              This message will be sent once per conversation per hour to avoid spam.
            </div>
          </div>
        )}
      </div>
      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* CHAT HISTORY                                                         */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="settings-form">

        <h3 style={{ fontWeight: 600, marginBottom: 4 }}>🗂 Chat History</h3>
        <p style={{ color: "#64748b", fontSize: 13, marginBottom: 16 }}>
          Manage all your messages across every conversation.
        </p>

        {historyMsg && (
          <div className={`mail-settings-message ${historyMsg.type === "error" ? "error" : "success"}`}
            style={{ marginBottom: 12 }}>
            {historyMsg.text}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Backup */}
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 12,
            padding: "14px 16px",
            border: "1px solid #e2e8f0", borderRadius: 10,
          }}>
            <div>
              <div style={{ fontWeight: 500, marginBottom: 3 }}>📦 Backup Chat History</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                Download all messages as a JSON file grouped by conversation.
              </div>
            </div>
            <button
              className="btn-save"
              onClick={handleBackup}
              disabled={backing}
              style={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              {backing ? "Preparing…" : backupDone ? "✔ Downloaded" : "Download Backup"}
            </button>
          </div>

          {/* Delete all */}
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 12,
            padding: "14px 16px",
            border: "1px solid #fee2e2", borderRadius: 10,
            background: "#fff5f5",
          }}>
            <div>
              <div style={{ fontWeight: 500, color: "#dc2626", marginBottom: 3 }}>
                🗑 Delete All Chat History
              </div>
              <div style={{ fontSize: 12, color: "#ef4444" }}>
                Permanently removes all your sent and received messages. Cannot be undone.
              </div>
            </div>
            <button
              onClick={handleDeleteAll}
              disabled={deleting}
              style={{
                background: "#ef4444", color: "#fff",
                border: "none", borderRadius: 8,
                padding: "8px 16px", cursor: "pointer",
                fontWeight: 500, fontSize: 13, flexShrink: 0,
                whiteSpace: "nowrap",
                opacity: deleting ? 0.7 : 1,
              }}
            >
              {deleting ? "Deleting…" : "Delete All"}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ChatSettings;
